import React, { memo, useMemo, useState } from 'react';
import {
  Box,
  ButtonSelect,
  Icon,
  InputField,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import SodAdvancedFilters from '../../../../../shared/components/SodControls/SodAdvancedFilters';
import {
  CF_NEU_STL,
  ENTITLEMENT_DEATILS_TABS,
} from '../../../../../shared/constants';
import {
  useInvertedLocalize,
  useNavigateTo,
  useTypedSelector,
} from '../../../../../shared/hooks';

import {
  ACTIVE_RISKS_FILTERS,
  RISKS_FILTERS,
  RISKS_TEMP_LIST_ITEMS_MOCK,
} from './data-layer/detailsMockData';
import { type EntitlementRisksRowType } from './data-layer/types';
import InherentRiskColumnList from './InherentRisksColumnList/InherentRiskColumnList';
import InherentRisksListGroup from './InherentRisksListGroup/InherentRisksListGroup';
import InherentRisksSidePanel from './InherentRisksSidePanel/InherentRisksSidePanel';

import './InherentRisks.css';

const initialOptionsWithIcons = [
  {
    label: 'View Violations',
    value: ENTITLEMENT_DEATILS_TABS.TAB_VIOLATIONS,
    icon: 'SOD',
  },
];

function EntitlementInherentRisks() {
  // Search
  const [searchValue, setSearchValue] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  // Asides
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [selectedRow, setSelectedRow] =
    useState<EntitlementRisksRowType | null>(null);
  const [isFilterBarOpen, setIsFilterBarOpen] = useState(false);

  const l = useInvertedLocalize();
  const { navigateToDetails } = useNavigateTo();
  const { isMenuPinned } = useTypedSelector((state) => state.user);

  const entitlementClasses = cx(
    'Sod-InherentRisks',
    isMenuPinned && 'Sod-InherentRisks--isMenuPinned'
  );

  const listAsideClasses = cx(
    'Sod-InherentRisks-main',
    isSidePanelOpen && 'Sod-InherentRisks-main--isSidePanelOpen'
  );

  const advancedFilterClasses = cx(
    'Sod-InherentRisks-SodAdvancedFilters',
    isFilterBarOpen
      ? 'SodAdvancedFilters--isOpen'
      : 'SodAdvancedFilters--isClose'
  );

  const handleCloseSidePanel = () => {
    setIsSidePanelOpen(false);
    setSelectedRow(null);
  };

  const handleOpenSidePanel = (row: EntitlementRisksRowType) => {
    setSelectedRow(row);
    setIsSidePanelOpen(true);
  };

  const handleSourceChange = (sourceType: string, activeTab: string) => {
    navigateToDetails({
      key: '1234567890',
      params: {
        title: 'OracleCloud_GL_SOD_Enter Journal & Post Journal',
        user: 'Joey Kookamunga',
        type: sourceType,
        activeTab,
      },
    });
  };

  const filteredListRows: EntitlementRisksRowType[] = useMemo(
    () =>
      RISKS_TEMP_LIST_ITEMS_MOCK.filter((row: EntitlementRisksRowType) =>
        row.title.toLowerCase().includes(searchValue.toLowerCase())
      ),
    [searchValue]
  );

  return (
    <Box className={entitlementClasses}>
      <Box tag='section' className={listAsideClasses}>
        {/* Main scroll area */}
        <Box className='Sod-InherentRisks-scrollArea'>
          <Box className='Sod-InherentRisks-listAndControls'>
            {/* Controls Wrapper */}
            <Box className='Sod-InherentRisks-controlsWrapper'>
              {/* Controls */}
              <Box className='Sod-InherentRisks-controls'>
                <Box className='Sod-InherentRisks-controlsLeft'>
                  <InputField
                    name='basic'
                    placeholder='Search'
                    kind='search'
                    size='medium'
                    prefixIcon={<Icon kind='Search' color={CF_NEU_STL} />}
                    value={searchValue}
                    setValue={setSearchValue}
                    isSearchExpanded={isSearchExpanded}
                    setIsSearchExpanded={setIsSearchExpanded}
                    classNames={{
                      inputWrapper: 'Sod-InherentRisks-searchInput',
                    }}
                  />
                  <ButtonSelect
                    kind='rectangle'
                    label={l('Filter')}
                    onClick={() => setIsFilterBarOpen(!isFilterBarOpen)}
                    isOpen={false}
                    size='medium'
                    prefixIcon={<Icon kind='Filter' />}
                    isVisualCueEnabled={false}
                  />
                </Box>
              </Box>
              <SodAdvancedFilters
                filters={RISKS_FILTERS}
                activeFilters={ACTIVE_RISKS_FILTERS}
                isClearBtn={false}
                searchDebounce={400}
                handleFilterChange={() => {}}
                handleFilterSearch={() => Promise.resolve()}
                handleOptionsClick={() => {}}
                handleClearBtn={() => {}}
                className={advancedFilterClasses}
              />
              {/* Subheading */}
              <Box className='Sod-InherentRisks-listSubheadingWrapper'>
                <Typography kind='body3' color={CF_NEU_STL}>
                  {`${filteredListRows.length} ${l('Items')}`}
                </Typography>
                <Box className='Sod-InherentRisks-sortViewGroup'>
                  <ButtonSelect
                    kind='rectangle'
                    color='transparent'
                    label={l('Sort')}
                    onClick={() => {}}
                    isOpen={false}
                    size='small'
                    prefixIcon={<Icon kind='Sort' />}
                    isVisualCueEnabled={false}
                  />
                  <span className='Sod-InherentRisks-buttonDividerLine' />
                  <InherentRiskColumnList />
                </Box>
              </Box>
            </Box>
            {/* List */}
            <InherentRisksListGroup
              handleOpenSidePanel={handleOpenSidePanel}
              selectedRow={selectedRow}
              listRows={filteredListRows}
              isSidePanelOpen={isSidePanelOpen}
              iconType='CriticalHighRiskHexagon'
              handleSourceChange={handleSourceChange}
              initialOptionsWithIcons={initialOptionsWithIcons}
            />
          </Box>
        </Box>
        {/* Entitlement Asides */}
        <Box className='Sod-InherentRisks-asides'>
          <InherentRisksSidePanel
            isSidePanelOpen={isSidePanelOpen}
            handleCloseSidePanel={handleCloseSidePanel}
            selectedRow={selectedRow}
            handleSourceChange={handleSourceChange}
            initialOptionsWithIcons={initialOptionsWithIcons}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default memo(EntitlementInherentRisks);
